import type AttachmentDataProviderType from 'owa-service/lib/contract/AttachmentDataProviderType';
import type FetchDataProviderResourceResponse from 'owa-service/lib/contract/FetchDataProviderResourceResponse';
import fetchDataProviderResourceRequest from 'owa-service/lib/factory/fetchDataProviderResourceRequest';
import fetchDataProviderResourceOperation from 'owa-service/lib/operation/fetchDataProviderResourceOperation';
import { isSuccessStatusCode, HttpStatusCode } from 'owa-http-status-codes';
import { AssetErrorType } from 'owa-timestream-asset-sdk';

/**
 * Fetches the large thumbnail url for a shared file.
 * @param providerType file provider type
 * @param sharedUrl the generated share url for a given file
 */
export async function getFileThumbnailUrl(
    providerType: AttachmentDataProviderType,
    sharedUrl: string,
    onError?: (errorType: AssetErrorType) => void
): Promise<string> {
    try {
        const response = await fetchDriveItemInternal(providerType, sharedUrl, '/thumbnails');
        if (isSuccessStatusCode(response!.Status!) && response.Body) {
            const result = JSON.parse(response.Body);
            return result.value[0].large.url;
        } else if (response!.Status === HttpStatusCode.NotFound) {
            onError?.(AssetErrorType.NotFound);
        } else if (
            response!.Status === HttpStatusCode.Unauthorized ||
            response!.Status === HttpStatusCode.Forbidden
        ) {
            onError?.(AssetErrorType.Unauthorized);
        }
    } catch (err) {
        return '';
    }

    return '';
}

/**
 * Constructs a request object for a drive item & any of its relationship info if specified.
 * API doc for url construction: https://docs.microsoft.com/en-us/onedrive/developer/rest-api/api/shares_get?view=odsp-graph-online#http-request
 * API doc for relationships: https://docs.microsoft.com/en-us/onedrive/developer/rest-api/resources/driveitem?view=odsp-graph-online#relationships
 * @param providerType file provider type
 * @param sharedUrl the generated share url for a given file
 * @param relationshipExtension url extension to query any driveItem relationships
 */
export async function fetchDriveItemInternal(
    providerType: AttachmentDataProviderType,
    sharedUrl: string,
    relationshipExtension?: string
): Promise<FetchDataProviderResourceResponse> {
    const base64Value: string = btoa(sharedUrl);
    const base64EncodedUrl: string =
        'u!' +
        base64Value
            .replace(/^\=+|\=+$/g, '')
            .replace('/', '_')
            .replace('+', '-');
    const schemaHost = new RegExp(/.+?\:\/\/.+?(\/)/g)?.exec(sharedUrl)?.[0];
    let vroomUrl = `${schemaHost}_api/v2.0/shares/${base64EncodedUrl}/driveItem`;
    if (!!relationshipExtension) {
        vroomUrl += relationshipExtension;
    }

    const requestObj = fetchDataProviderResourceRequest({
        ProviderType: providerType,
        Url: vroomUrl,
    });
    return fetchDataProviderResourceOperation(requestObj).then(response => response);
}
